'use client';

import type { ToCamelCaseKeys } from '@core/types';
import { toSnakeCaseKeys } from 'es-toolkit/object';

import { useOnceEffect } from '@/hooks/useOnceEffect';
import type { GTM_EVENT_TYPE, GtmEventParams } from '@/utils/analytics/gtm';
import { trackGtmEvent } from '@/utils/analytics/gtm';

type GtmEventTriggerProps<T extends GTM_EVENT_TYPE> = {
  /** GTM 이벤트 타입 */
  type: T;
} & ToCamelCaseKeys<GtmEventParams[T]>;

/**
 * 페이지에 진입 했을 때 GTM 이벤트를 자동으로 트리거하는 컴포넌트
 *
 * @example
 * ```tsx
 * // VIEW_CONTENT 이벤트
 * <GtmEventTrigger
 *   type={GTM_EVENT_TYPE.VIEW_CONTENT}
 *   contentName="정책명"
 *   contentCategory={CONTENT_CATEGORY.POLICY}
 *   policyAgency="기관명"
 * />
 *
 * // SELECT_ITEM 이벤트
 * <GtmEventTrigger
 *   type={GTM_EVENT_TYPE.SELECT_ITEM}
 *   currency="KRW"
 *   value={10000}
 *   localName="지역명"
 *   items={[...]}
 * />
 * ```
 */
export const GtmEventTrigger = <T extends GTM_EVENT_TYPE>({
  type,
  ...params
}: GtmEventTriggerProps<T>) => {
  useOnceEffect(() => {
    trackGtmEvent(
      type,
      toSnakeCaseKeys(params) as unknown as GtmEventParams[T],
    );
  });

  return null;
};
